import { Line } from "@ant-design/charts";
import { LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Col, Empty, Form, Row, Segmented, Table } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { parseDateToLocal } from "../../helpers/ParserHelper";
import { TableHelper } from "../../helpers/TableHelper";
import { get_language, translate } from "../../locale";
import SelfMeasureServices from "../../services/SelfMeasuerServices";
import CustomDatePicker from "../Datepicker/CustomDatePicker";
import SectionLoader from "../Loaders/SectionLoader";
import RangeForm from "./RangeForm";

const StepsCharts = () => {
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedGraph, setSelectedGraph] = useState("graph");
  const [dateFrom, setDateFrom] = useState(
    new Date(moment().subtract(7, "days"))
  );
  const [dateTo, setDateTo] = useState(new Date());
  const { username } = useParams();
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
      total: 0,
    },
  });
  const [total, setTotal] = useState(0);

  const [configGraph, setConfigGraph] = useState({
    data: [{ year: moment().format("DD/MM/YYYY HH:mm"), value: 0 }],
    height: 500,
    width: "100%",
    xField: "year",
    yField: "value",

    yAxis: {
      max: 100000,
      min: 0,
      tickInterval: 10000,
      title: {
        text: "Pasos",
      },
    },
    geometryOptions: [
      {
        geometry: "line",
        color: "#5B8FF9",
      },
    ],
    point: {
      size: 5,
      shape: "diamond | circule",
    },
    legend: {
      layout: "horizontal",
      position: "bottom",
      offsetY: 0,
    },
  });

  const [dataList, setDataList] = useState([]);

  const columnsList = [
    {
      title: "Fecha",
      dataIndex: "date",
      key: "date",
      render: (_, record) => moment(record.date).format("DD/MM/YYYY"),
      sorter: { multiple: 1 },
    },
    {
      title: "Pasos",
      dataIndex: "value",
      key: "value",
      render: (_, record) => {
        return `${record.value}`;
      },
      sorter: { multiple: 2 },
    },
  ];

  useEffect(() => {
    loadGraph();
    loadTotalGraph();
  }, [JSON.stringify(tableParams)]);

  const loadGraph = async () => {
    setLoading(true);
    try {
      let pagination = {
        page_size: tableParams.pagination.pageSize,
        page_num: tableParams.pagination.current,
      };
      const sorter = TableHelper.sorter(tableParams);
      if (sorter !== null) {
        pagination = { ...pagination, order_by: sorter };
      }
      const res = await SelfMeasureServices.getStepsHistory(
        moment(dateFrom).format("YYYY-MM-DD"),
        moment(dateTo).format("YYYY-MM-DD"),
        "range",
        username,
        pagination
      );
      if (res.status >= 200 && res.status < 300) {
        const list = res.data.controls.map((i) => ({
          date: i.date,
          value: i.value,
        }));
        setDataList(list);
        setTotal(res.data.total_pages * tableParams.pagination.pageSize);
      }

      setLoading(false);
      setSearching(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      setSearching(false);
    }
  };
  const calculateMaxYAxis = (data) => {
    const maxValue = Math.max(...data.map((item) => item.value));
    let max, tickInterval;

    if (maxValue < 100) {
      max = 100;
      tickInterval = 10;
    } else if (maxValue < 1000) {
      max = 1000;
      tickInterval = 100;
    } else if (maxValue < 5000) {
      max = 5000;
      tickInterval = 500;
    } else if (maxValue < 10000) {
      max = 10000;
      tickInterval = 1000;
    } else if (maxValue < 50000) {
      max = 50000;
      tickInterval = 5000;
    } else {
      max = 100000;
      tickInterval = 10000;
    }

    return { max, tickInterval };
  };

  // Load ALL data in the graph
  const loadTotalGraph = async () => {
    setLoading(true);
    try {
      let pagination = {
        page_size: tableParams.pagination.pageSize,
        page_num: tableParams.pagination.current,
      };
      const sorter = TableHelper.sorter(tableParams);
      if (sorter !== null) {
        pagination = { ...pagination, order_by: sorter };
      }
      const res = await SelfMeasureServices.getStepsHistory(
        moment(dateFrom).format("YYYY-MM-DD"),
        moment(dateTo).format("YYYY-MM-DD"),
        "range",
        username,
        pagination
      );
      if (res.status >= 200 && res.status < 300) {
        const data = res.data.controls.map((i) => ({
          value: i.value,
          year: parseDateToLocal(i.date),
        }));
        const { max, tickInterval } = calculateMaxYAxis(data);
        setConfigGraph((v) => ({
          ...v,
          yAxis: { ...v.yAxis, max, tickInterval },
          data: data.reverse(),
        }));
      }

      setLoading(false);
      setSearching(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      setSearching(false);
    }
  };

  const handleChangeItem = (value) => {
    setSelectedGraph(value);
    switch (value) {
      case "graph":
        loadTotalGraph();
        loadGraph();
        break;
      case "list":
        loadGraph();
        break;
      case "range":
        break;
    }
  };
  const onChangeDatePicker = (name, value) => {
    if (name === "dateFrom") {
      setDateFrom(value);
    } else if (name === "dateTo") {
      setDateTo(value);
    }
  };
  const handleSearch = (e) => {
    setSearching(true);
    loadTotalGraph();
    loadGraph();
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    });

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setDataList([]);
      setConfigGraph([]);
    }
  };

  return (
    <>
      {selectedGraph !== "range" && (
        <Form onFinish={handleSearch}>
          <Row className="mt-6 mb-14 relative z-10" gutter={[40, 10]}>
            <Col xs={{ span: 24 }} md={{ span: 8 }} lg={{ span: 6 }}>
              <div className="flex flex-row flex-no-wrap items-stretch justify-end md:justify-start">
                <div className="flex flex-col justify-center mr-4 md:mr-0 lg:mr-4 text-sm md:text-xs lg:text-sm">
                  {translate("Fecha desde")}
                </div>
                <CustomDatePicker
                  locale={get_language}
                  selected={dateFrom}
                  onChange={(e) => onChangeDatePicker("dateFrom", e)}
                />
              </div>
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 8 }} lg={{ span: 6 }}>
              <div className="flex flex-row flex-no-wrap items-stretch justify-end md:justify-start">
                <div className="flex flex-col justify-center mr-4 md:mr-0 lg:mr-4 text-sm md:text-xs lg:text-sm">
                  {translate("Fecha hasta")}
                </div>
                <CustomDatePicker
                  locale={get_language}
                  selected={dateTo}
                  onChange={(e) => onChangeDatePicker("dateTo", e)}
                />
              </div>
            </Col>
            <Col
              xs={{ span: 24 }}
              md={{ span: 6 }}
              className="text-right md:text-left"
            >
              <Button
                htmlType="submit"
                type="primary"
                className="bg-custom_primary"
                icon={
                  searching ? (
                    <LoadingOutlined />
                  ) : (
                    <SearchOutlined style={{ position: "relative" }} />
                  )
                }
              >
                {translate("Buscar")}
              </Button>
            </Col>
          </Row>
        </Form>
      )}
      <Row className="mb-14">
        <Col>
          <Segmented
            options={[
              { value: "graph", label: "Gráfico" },
              { value: "list", label: "Listado" },
              { value: "range", label: "Rangos" },
            ]}
            onChange={handleChangeItem}
          />
        </Col>
      </Row>
      <Row className="relative mb-14">
        {loading && <SectionLoader />}

        {selectedGraph === "graph" && (
          <Col span={24}>
            <Line {...configGraph} />
          </Col>
        )}
        {selectedGraph === "list" && (
          <Col span={24}>
            <Table
              rowKey={(i) => i.date}
              columns={columnsList}
              dataSource={dataList}
              locale={{
                emptyText: <Empty description={translate("No hay datos")} />,
                sortTitle: translate("Ordenar"),
                triggerDesc: translate("Ordenar (descendente)"),
                triggerAsc: translate("Ordenar (ascendente)"),
                cancelSort: translate("Cancelar orden"),
              }}
              pagination={{ ...tableParams.pagination, total: total }}
              onChange={handleTableChange}
            />
          </Col>
        )}
        {selectedGraph === "range" && (
          <Col span={24}>
            <Row gutter={[48, 48]}>
              <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <RangeForm
                  initialValues={{
                    patient: username,
                    type: "pasos",
                    max_value_alert: 100000,
                    max_value: 50000,
                    min_value: 7000,
                    min_value_alert: 3000,
                  }}
                  title={translate("Rango de Pasos")}
                />
              </Col>
            </Row>
          </Col>
        )}
      </Row>
    </>
  );
};

export default StepsCharts;
